/**
 * Écran mobile : Créer ou modifier un événement
 * Mode create (sans :id) ou edit (avec :id dans l'URL)
 */
import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BookOpen, Palmtree, PartyPopper, Check, Search, X, Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useMyMatieres, useCreateEvenement, useMyEvents, useUpdateEvenement, useDeleteEvenement, useMyVacancesPeriodes, useMyPeriodesExclues } from '@/features/events/hooks/use-events'
import { InlineDatePicker } from '@/shared/components/inline-date-picker'
import type { TypeEvenement, CreateEvenementRequest } from '@/shared/types/evenement'

/* ------------------------------------------------------------------ */
/*  Config par type                                                    */
/* ------------------------------------------------------------------ */

function buildTypes(t: (key: string) => string) {
  return [
    {
      key: 'CONTROLE' as TypeEvenement,
      label: t('mobile.eventCreate.testLabel'),
      description: t('mobile.eventCreate.testDescription'),
      icon: BookOpen,
      bg: 'bg-red-50',
      text: 'text-red-600',
      borderActive: 'border-red-400 ring-2 ring-red-200',
    },
    {
      key: 'VACANCES' as TypeEvenement,
      label: t('mobile.eventCreate.vacationLabel'),
      description: t('mobile.eventCreate.vacationDescription'),
      icon: Palmtree,
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      borderActive: 'border-blue-400 ring-2 ring-blue-200',
    },
    {
      key: 'RELAX' as TypeEvenement,
      label: t('mobile.eventCreate.relaxLabel'),
      description: t('mobile.eventCreate.relaxDescription'),
      icon: PartyPopper,
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      borderActive: 'border-amber-400 ring-2 ring-amber-200',
    },
  ]
}

/* ------------------------------------------------------------------ */
/*  Shared input class                                                 */
/* ------------------------------------------------------------------ */

const inputClass =
  'h-11 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm text-neutral-900 transition-all placeholder:text-neutral-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function EventCreateMobilePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const types = buildTypes(t)
  const { id: paramId } = useParams<{ id: string }>()
  const editId = paramId ? Number(paramId) : null
  const isEditMode = editId !== null

  const { data: matieres, isLoading: matieresLoading } = useMyMatieres()
  const { data: events } = useMyEvents()
  const { data: vacancesPeriodes } = useMyVacancesPeriodes()
  const { data: periodesExclues } = useMyPeriodesExclues()
  const createEvenement = useCreateEvenement()
  const updateEvenement = useUpdateEvenement()
  const deleteEvenement = useDeleteEvenement()

  const [type, setType] = useState<TypeEvenement | null>(null)

  // Contrôle fields
  const [matiereId, setMatiereId] = useState<number | null>(null)
  const [matiereSearch, setMatiereSearch] = useState('')
  const [dateControle, setDateControle] = useState('')

  const allMatieres = matieres ?? []

  const filteredMatieres = useMemo(() => {
    if (!matiereSearch.trim()) return allMatieres
    const q = matiereSearch.toLowerCase()
    return allMatieres.filter((m) => m.nom.toLowerCase().includes(q))
  }, [matiereSearch, allMatieres])

  const selectedMatiere = allMatieres.find((m) => m.id === matiereId)

  // Vacances fields
  const [dateDebutVacances, setDateDebutVacances] = useState('')

  // Relax fields
  const [dateRelax, setDateRelax] = useState('')
  const [dateFinRelax, setDateFinRelax] = useState('')
  const [libelleRelax, setLibelleRelax] = useState('')

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // Pre-fill form in edit mode
  const [initialized, setInitialized] = useState(false)
  useEffect(() => {
    if (!isEditMode || initialized || !events) return
    const event = events.find((e) => e.id === editId)
    if (!event) return

    setType(event.type)
    switch (event.type) {
      case 'CONTROLE':
        setMatiereId(event.matiereId)
        setDateControle(event.dateDebut)
        break
      case 'VACANCES':
        setDateDebutVacances(event.dateDebut)
        break
      case 'RELAX':
        setDateRelax(event.dateDebut)
        setDateFinRelax(event.dateFin ?? event.dateDebut)
        setLibelleRelax(event.libelle ?? '')
        break
    }
    setInitialized(true)
  }, [isEditMode, editId, events, initialized])

  /* ----- Vacances date filter ----- */
  const isVacancesDate = useCallback((date: Date) => {
    if (!vacancesPeriodes?.length) return false
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    return vacancesPeriodes.some((p) => {
      const debut = new Date(p.dateDebut + 'T00:00:00')
      const fin = new Date(p.dateFin + 'T00:00:00')
      return d >= debut && d < fin
    })
  }, [vacancesPeriodes])

  /* ----- Contrôle date filter (exclure vacances + relax) ----- */
  const relaxEventDates = useMemo(() => {
    if (!events) return new Set<string>()
    const dates = new Set<string>()
    for (const e of events) {
      if (e.type === 'RELAX') {
        const start = new Date(e.dateDebut + 'T00:00:00')
        const end = e.dateFin ? new Date(e.dateFin + 'T00:00:00') : start
        const current = new Date(start)
        while (current <= end) {
          const d = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`
          dates.add(d)
          current.setDate(current.getDate() + 1)
        }
      }
    }
    return dates
  }, [events])

  const isControleDate = useCallback((date: Date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

    // Exclure les jours RELAX créés par l'étudiant
    if (relaxEventDates.has(dateStr)) return false

    // Exclure les périodes admin VACANCES + RELAX
    if (periodesExclues?.length) {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
      const isExcluded = periodesExclues.some((p) => {
        const debut = new Date(p.dateDebut + 'T00:00:00')
        const fin = new Date(p.dateFin + 'T00:00:00')
        return d >= debut && d < fin
      })
      if (isExcluded) return false
    }

    return true
  }, [periodesExclues, relaxEventDates])

  /* ----- Validation ----- */
  const isValid = (() => {
    if (!type) return false
    if (type === 'CONTROLE') return matiereId !== null && dateControle !== ''
    if (type === 'VACANCES') return dateDebutVacances !== ''
    if (type === 'RELAX') return dateRelax !== '' && dateFinRelax !== '' && dateFinRelax >= dateRelax && libelleRelax.trim() !== ''
    return false
  })()

  const isPending = createEvenement.isPending || updateEvenement.isPending

  /* ----- Submit ----- */
  const handleSubmit = () => {
    if (!type || !isValid) return

    let request: CreateEvenementRequest

    switch (type) {
      case 'CONTROLE':
        request = { type, matiereId: matiereId!, dateDebut: dateControle }
        break
      case 'VACANCES':
        request = { type, dateDebut: dateDebutVacances }
        break
      case 'RELAX':
        request = { type, dateDebut: dateRelax, dateFin: dateFinRelax || undefined, libelle: libelleRelax || undefined }
        break
    }

    if (isEditMode) {
      updateEvenement.mutate({ id: editId!, data: request }, {
        onSuccess: () => navigate('/events'),
      })
    } else {
      createEvenement.mutate(request, {
        onSuccess: () => navigate('/events'),
      })
    }
  }

  /* ----- Delete ----- */
  const handleDelete = () => {
    if (!editId) return
    deleteEvenement.mutate(editId, {
      onSuccess: () => navigate('/events'),
    })
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-primary/5 via-primary/[0.02] to-white">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-neutral-100 bg-white/90 backdrop-blur-md">
        <div className="flex items-center gap-3 px-4 pb-3 pt-3">
          <button
            onClick={() => navigate('/events')}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors active:bg-neutral-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="flex-1 pr-9 text-center text-lg font-bold text-neutral-900">
            {isEditMode ? t('mobile.eventCreate.editTitle') : t('mobile.eventCreate.createTitle')}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 pb-28 pt-5">
        {/* ----- Type selector ----- */}
        <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-neutral-400">
          {t('mobile.eventCreate.type')}
        </label>
        <div className="flex gap-2">
          {types.map((tp) => {
            const Icon = tp.icon
            const selected = type === tp.key
            return (
              <button
                key={tp.key}
                onClick={() => setType(tp.key)}
                className={cn(
                  'flex flex-1 flex-col items-center gap-1.5 rounded-xl border-2 py-2.5 transition-all',
                  selected ? tp.borderActive : 'border-neutral-100 active:border-neutral-200',
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                    selected ? tp.bg : 'bg-neutral-50',
                  )}
                >
                  <Icon className={cn('h-4 w-4', selected ? tp.text : 'text-neutral-400')} />
                </div>
                <span className={cn(
                  'text-xs font-bold',
                  selected ? 'text-neutral-900' : 'text-neutral-500',
                )}>
                  {tp.label}
                </span>
              </button>
            )
          })}
        </div>

        {/* ----- Conditional fields ----- */}
        {type && (
          <div className="mt-6 space-y-4">
            <div className="h-px bg-neutral-100" />

            {/* === CONTRÔLE === */}
            {type === 'CONTROLE' && (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                    {t('mobile.eventCreate.associateSubject')}
                  </label>

                  {matieresLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    </div>
                  ) : selectedMatiere ? (
                    <div className="flex items-center gap-2 rounded-xl border border-neutral-100 bg-neutral-50 p-3">
                      <span
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold"
                        style={{
                          backgroundColor: selectedMatiere.couleur + '18',
                          color: selectedMatiere.couleur,
                        }}
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: selectedMatiere.couleur }}
                        />
                        {selectedMatiere.nom}
                      </span>
                      <button
                        onClick={() => setMatiereId(null)}
                        className="ml-auto rounded-full p-1 text-neutral-400 transition-colors active:bg-neutral-200 active:text-neutral-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <input
                          type="text"
                          value={matiereSearch}
                          onChange={(e) => setMatiereSearch(e.target.value)}
                          placeholder={t('mobile.eventCreate.searchSubject')}
                          className={cn(inputClass, 'pl-10')}
                        />
                      </div>

                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {matiereSearch.trim() && filteredMatieres.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => {
                              setMatiereId(m.id)
                              setMatiereSearch('')
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 transition-all active:scale-95"
                            style={{ borderColor: m.couleur + '40' }}
                          >
                            <span
                              className="h-2.5 w-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: m.couleur }}
                            />
                            {m.nom}
                          </button>
                        ))}
                        {matiereSearch.trim() && filteredMatieres.length === 0 && (
                          <p className="py-2 text-xs text-neutral-400">{t('mobile.eventCreate.noSubjectFound')}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                    {t('mobile.eventCreate.testDate')}
                  </label>
                  <InlineDatePicker
                    value={dateControle}
                    onChange={setDateControle}
                    accent="#dc2626"
                    isDateEnabled={isControleDate}
                  />
                  <p className="mt-1.5 text-xs text-neutral-400">
                    {t('mobile.eventCreate.testDateHint')}
                  </p>
                </div>
              </>
            )}

            {/* === VACANCES === */}
            {type === 'VACANCES' && (
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                  {t('mobile.eventCreate.vacationStart')}
                </label>
                <InlineDatePicker
                  value={dateDebutVacances}
                  onChange={setDateDebutVacances}
                  accent="#2563eb"
                  isDateEnabled={isVacancesDate}
                />
                <p className="mt-1.5 text-xs text-neutral-400">
                  {t('mobile.eventCreate.vacationDateHint')}
                </p>
              </div>
            )}

            {/* === RELAX === */}
            {type === 'RELAX' && (
              <>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                    {t('mobile.eventCreate.restDayStart')}
                  </label>
                  <InlineDatePicker
                    value={dateRelax}
                    onChange={(v) => {
                      setDateRelax(v)
                      if (dateFinRelax && v > dateFinRelax) setDateFinRelax(v)
                    }}
                    accent="#d97706"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                    {t('mobile.eventCreate.restDayEnd')}
                  </label>
                  <InlineDatePicker
                    value={dateFinRelax}
                    onChange={setDateFinRelax}
                    accent="#d97706"
                    isDateEnabled={(date) => {
                      if (!dateRelax) return true
                      const d = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                      return d >= dateRelax
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-neutral-400">
                    {t('mobile.eventCreate.label')}
                  </label>
                  <input
                    type="text"
                    value={libelleRelax}
                    onChange={(e) => setLibelleRelax(e.target.value.slice(0, 20))}
                    placeholder={t('mobile.eventCreate.labelPlaceholder')}
                    maxLength={20}
                    className={inputClass}
                  />
                  <p className="mt-1 text-right text-[10px] text-neutral-400">
                    {libelleRelax.length}/20
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {/* Delete button (edit mode only) */}
        {isEditMode && type && (
          <div className="mt-8">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-red-100 py-3 text-sm font-bold text-red-500 transition-all active:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                {t('mobile.eventCreate.deleteEvent')}
              </button>
            ) : (
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                <p className="text-center text-sm font-medium text-red-600">
                  {t('mobile.eventCreate.deleteConfirm')}
                </p>
                <div className="mt-3 flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 rounded-lg bg-white py-2.5 text-sm font-bold text-neutral-600 shadow-sm transition-all active:scale-[0.98]"
                  >
                    {t('mobile.eventCreate.cancel')}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteEvenement.isPending}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-500 py-2.5 text-sm font-bold text-white shadow-sm transition-all active:scale-[0.98]"
                  >
                    {deleteEvenement.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    {t('mobile.eventCreate.confirm')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validate button */}
      <div className="fixed inset-x-4 bottom-[max(env(safe-area-inset-bottom,16px),16px)] z-50">
        <button
          onClick={handleSubmit}
          disabled={!isValid || isPending}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-sm font-bold transition-all',
            isValid && !isPending
              ? 'bg-primary text-white shadow-xl shadow-primary/30 active:scale-[0.98]'
              : 'bg-neutral-100 text-neutral-400 cursor-not-allowed',
          )}
        >
          {isPending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Check className="h-5 w-5" strokeWidth={2.5} />
          )}
          {isPending
            ? (isEditMode ? t('mobile.eventCreate.updating') : t('mobile.eventCreate.creating'))
            : t('mobile.eventCreate.validate')}
        </button>
      </div>
    </div>
  )
}
